import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { money, errMsg } from '../../utils/helpers';
import { Spinner, EmptyState, Modal, ConfirmModal, Field, Select } from '../../components/common/UI';
import { Plus, Edit2, Trash2, Package } from 'lucide-react';
import toast from 'react-hot-toast';

export default function CatalogPage() {
  const { isProducer } = useAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState(null);
  const [catModal, setCatModal] = useState(null);
  const [productModal, setProductModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', sort_order: 0 });
  const [productForm, setProductForm] = useState({ category_id: '', name: '', sku: '', last_price: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const loadCategories = async () => {
    const { data } = await api.get('/catalog/categories');
    setCategories(data);
  };

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { active_only: 'false' };
      if (selectedCat) params.category_id = selectedCat;
      const { data } = await api.get('/catalog/products', { params });
      setProducts(data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [selectedCat]);

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  const saveCat = async () => {
    setSaving(true);
    try {
      if (!catModal.id) { await api.post('/catalog/categories', catForm); toast.success('Категория добавлена'); }
      else { await api.patch('/catalog/categories/' + catModal.id, catForm); toast.success('Сохранено'); }
      setCatModal(null); loadCategories();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteCat = async (id) => {
    try { await api.delete('/catalog/categories/' + id); toast.success('Категория удалена'); loadCategories(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const saveProduct = async () => {
    setSaving(true);
    try {
      if (!productModal.id) { await api.post('/catalog/products', productForm); toast.success('Товар добавлен'); }
      else { await api.patch('/catalog/products/' + productModal.id, productForm); toast.success('Сохранено'); }
      setProductModal(null); loadProducts();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteProduct = async (id) => {
    try { await api.delete('/catalog/products/' + id); toast.success('Товар деактивирован'); loadProducts(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const catOpts = categories.map(c => ({ value: c.id, label: c.name }));

  const openNewProduct = () => {
    setProductForm({ category_id: selectedCat || '', name: '', sku: '', last_price: '', notes: '', is_active: true });
    setProductModal({});
  };

  const openEditProduct = (p) => {
    setProductForm({ category_id: p.category_id || '', name: p.name, sku: p.sku || '', last_price: p.last_price || '', notes: p.notes || '', is_active: p.is_active });
    setProductModal(p);
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-100">Справочник товаров</h1>
          <p className="text-xs text-gray-600 mt-0.5">{products.length} позиций</p>
        </div>
        {isProducer && (
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => { setCatForm({ name: '', sort_order: 0 }); setCatModal({}); }}>
              <Plus size={14} /> Категория
            </button>
            <button className="btn-primary" onClick={openNewProduct}>
              <Plus size={14} /> Товар
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-4">
        {/* Sidebar */}
        <div className="w-48 shrink-0 space-y-1">
          <button onClick={() => setSelectedCat(null)}
            className={'w-full text-left px-3 py-2 rounded text-sm transition-colors ' + (!selectedCat ? 'bg-blue-900/40 text-blue-300' : 'text-gray-400 hover:bg-gray-800')}>
            Все категории
          </button>
          {categories.map(c => (
            <div key={c.id} className="flex items-center gap-1 group">
              <button onClick={() => setSelectedCat(c.id === selectedCat ? null : c.id)}
                className={'flex-1 text-left px-3 py-2 rounded text-sm transition-colors ' + (selectedCat === c.id ? 'bg-blue-900/40 text-blue-300' : 'text-gray-400 hover:bg-gray-800')}>
                {c.name}
              </button>
              {isProducer && (
                <div className="flex opacity-0 group-hover:opacity-100">
                  <button className="btn-ghost p-1" onClick={() => { setCatForm({ name: c.name, sort_order: c.sort_order }); setCatModal(c); }}><Edit2 size={12} /></button>
                  <button className="btn-ghost p-1 hover:text-red-400" onClick={() => setDeleteConfirm({ type: 'cat', id: c.id, name: c.name })}><Trash2 size={12} /></button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Products */}
        <div className="flex-1 card overflow-hidden">
          {loading ? <div className="flex justify-center py-12"><Spinner size={24} /></div>
          : products.length === 0 ? <EmptyState text="Нет товаров" />
          : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Название</th>
                  <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-32">Артикул</th>
                  <th className="text-right px-4 py-3 text-xs text-gray-500 font-medium w-28">Цена</th>
                  {isProducer && <th className="w-20 px-4 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className={'border-b border-gray-800/60 hover:bg-gray-800/30 transition-colors ' + (!p.is_active ? 'opacity-40' : '')}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Package size={13} className="text-gray-600 shrink-0" />
                        <span className="text-gray-200">{p.name}</span>
                        {!p.is_active && <span className="text-xs text-gray-600">(неакт.)</span>}
                      </div>
                      <div className="text-xs text-gray-600 ml-5">{p.category_name}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 mono text-xs">{p.sku || '—'}</td>
                    <td className="px-4 py-3 text-right mono text-gray-300">{money(p.last_price)}</td>
                    {isProducer && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button className="btn-ghost p-1.5" onClick={() => openEditProduct(p)}><Edit2 size={14} /></button>
                          <button className="btn-ghost p-1.5 hover:text-red-400" onClick={() => setDeleteConfirm({ type: 'product', id: p.id, name: p.name })}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {catModal && (
        <Modal title={catModal.id ? 'Редактировать категорию' : 'Новая категория'} onClose={() => setCatModal(null)} size="sm">
          <div className="space-y-3">
            <Field label="Название" required><input className="input" value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} autoFocus /></Field>
            <Field label="Порядок сортировки"><input type="number" className="input" value={catForm.sort_order} onChange={e => setCatForm(f => ({ ...f, sort_order: +e.target.value }))} /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setCatModal(null)}>Отмена</button>
              <button className="btn-primary" onClick={saveCat} disabled={saving}>{saving ? <Spinner size={14} /> : 'Сохранить'}</button>
            </div>
          </div>
        </Modal>
      )}

      {productModal && (
        <Modal title={productModal.id ? 'Редактировать товар' : 'Новый товар'} onClose={() => setProductModal(null)} size="sm">
          <div className="space-y-3">
            <Field label="Категория">
              <Select value={productForm.category_id} onChange={v => setProductForm(f => ({ ...f, category_id: v }))}
                options={catOpts} placeholder="Без категории" />
            </Field>
            <Field label="Название" required><input className="input" value={productForm.name} onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))} autoFocus /></Field>
            <Field label="Артикул / SKU">
              <div className="flex gap-2">
                <input className="input mono flex-1" value={productForm.sku}
                  onChange={e => setProductForm(f => ({ ...f, sku: e.target.value }))}
                  placeholder="Введите или сгенерируйте" />
                <button type="button" className="btn-secondary shrink-0 text-xs"
                  onClick={() => {
                    const prefix = productForm.name ? productForm.name.slice(0,3).toUpperCase().replace(/\s/g,'') : 'SKU';
                    const num = String(Date.now()).slice(-5);
                    setProductForm(f => ({ ...f, sku: prefix + '-' + num }));
                  }}>Авто</button>
              </div>
            </Field>
            <Field label="Цена, ₽" hint="Подставляется при добавлении в заказ">
              <input type="number" className="input" value={productForm.last_price} onChange={e => setProductForm(f => ({ ...f, last_price: e.target.value }))} />
            </Field>
            <Field label="Примечания"><textarea className="input resize-none" rows={2} value={productForm.notes} onChange={e => setProductForm(f => ({ ...f, notes: e.target.value }))} /></Field>
            {productModal.id && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="accent-blue-500 w-4 h-4" checked={productForm.is_active !== false}
                  onChange={e => setProductForm(f => ({ ...f, is_active: e.target.checked }))} />
                <span className="text-sm text-gray-300">Активен</span>
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setProductModal(null)}>Отмена</button>
              <button className="btn-primary" onClick={saveProduct} disabled={saving}>{saving ? <Spinner size={14} /> : 'Сохранить'}</button>
            </div>
          </div>
        </Modal>
      )}

      {deleteConfirm && (
        <ConfirmModal message={'Удалить "' + deleteConfirm.name + '"?'}
          onConfirm={() => { if (deleteConfirm.type === 'cat') deleteCat(deleteConfirm.id); else deleteProduct(deleteConfirm.id); }}
          onClose={() => setDeleteConfirm(null)} />
      )}
    </div>
  );
}
